import { useCallback, useRef, useState } from 'react';
import { useToast } from '@cbhq/cds-web/overlays/useToast';
import { ActionType, ComponentType, logEvent } from '@cbhq/client-analytics';
import { ApiError, NetworkError } from '@cbhq/data-layer';
import { defineMessages, useIntl } from '@cbhq/i18n';
import { useExportWorkspaceAuditEvents } from '@cbhq/identity-api-hooks/account-management/mutation';

import { logError } from '../../../utils/logging';
import { downloadCsvFromBase64, getExportFilename, getLast90DaysTimeRange } from '../utils/helpers';

const ANALYTICS_META = { componentType: ComponentType.api, action: ActionType.api };

const messages = defineMessages({
  exportComplete: {
    id: 'Activity.auditExport.complete',
    defaultMessage: 'Export complete. Your download will begin shortly.',
    description: 'Toast message shown when an activity CSV export has finished downloading',
  },
  nothingToExport: {
    id: 'Activity.auditExport.nothingToExport',
    defaultMessage: 'No activity to export.',
    description: 'Toast message shown when there is no activity to export',
  },
  exportError: {
    id: 'Activity.auditExport.error',
    defaultMessage: 'Export failed. Please try again.',
    description: 'Toast message shown when an activity export fails',
  },
});

export type AuditExportFilters = {
  actorIds: string[];
  eventTypes: string[];
  startTime?: string;
  endTime?: string;
};

export type UseAuditExportResult = {
  exportLast90Days: () => void;
  exportCustomReport: (filters: AuditExportFilters) => Promise<boolean>;
  isExporting: boolean;
};

export function useAuditExport(organizationId: string): UseAuditExportResult {
  const { formatMessage } = useIntl();
  const toast = useToast();
  const exportAuditEvents = useExportWorkspaceAuditEvents();

  // Synchronous guard closes the double-click race; the state drives the button UI.
  const isExportingRef = useRef(false);
  const [isExporting, setIsExporting] = useState(false);

  const handleExportError = useCallback(
    (error: unknown) => {
      logError(error, { context: 'useAuditExport', isHandled: true });
      toast.show(formatMessage(messages.exportError), { variant: 'bgNegative' });
      logEvent('activity_export_failed', ANALYTICS_META);
    },
    [formatMessage, toast],
  );

  const runExport = useCallback(
    async (payload: Parameters<typeof exportAuditEvents>[0]) => {
      if (isExportingRef.current) {
        return false;
      }
      isExportingRef.current = true;
      setIsExporting(true);

      try {
        const result = await exportAuditEvents({
          ...payload,
          organizationId,
          format: 'csv',
        });

        if (result instanceof NetworkError || result instanceof ApiError) {
          throw result;
        }
        if (!result.content) {
          toast.show(formatMessage(messages.nothingToExport));
          return false;
        }

        const { content, contentType } = result;
        // Always generate the filename client-side so it carries a timestamp; the
        // backend filename is date-only.
        downloadCsvFromBase64(content, getExportFilename(), contentType);
        toast.show(formatMessage(messages.exportComplete));
        logEvent('activity_export_success', ANALYTICS_META);
        return true;
      } catch (error) {
        handleExportError(error);
        return false;
      } finally {
        isExportingRef.current = false;
        setIsExporting(false);
      }
    },
    [exportAuditEvents, organizationId, toast, formatMessage, handleExportError],
  );

  const exportLast90Days = useCallback(() => {
    void runExport(getLast90DaysTimeRange());
  }, [runExport]);

  const exportCustomReport = useCallback(
    (filters: AuditExportFilters) => runExport(filters),
    [runExport],
  );

  return { exportLast90Days, exportCustomReport, isExporting };
}
